import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiDeleteBinLine, RiSurveyLine } from '@remixicon/react'
import type {
  SurveyQuestionInput,
  SurveyTemplateDetail,
  SurveyTemplateListItem,
  SurveyTemplateStatus,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Select,
  Switch,
  Textarea,
} from '../../components/ui'
import QuestionBuilder from '../../components/survey/QuestionBuilder'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { TEMPLATE_STATUS_TONE } from '../../lib/status'
import { formatDate } from '../../lib/dates'
import { cx, focusRing } from '../../lib/utils'

const BASE = '/api/admin/survey-templates'

export default function SurveyTemplates() {
  const { t, i18n } = useTranslation()

  const [templates, setTemplates] = useState<SurveyTemplateListItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<SurveyTemplateDetail | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState<SurveyTemplateStatus>('draft')
  const [isKickoff, setIsKickoff] = useState(false)
  const [questions, setQuestions] = useState<SurveyQuestionInput[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadList = useCallback(async () => {
    const response = await api.get<{ data: SurveyTemplateListItem[] }>(BASE)
    setTemplates(response.data)
  }, [])

  useEffect(() => {
    loadList()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [loadList])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }

    let cancelled = false
    api
      .get<{ data: SurveyTemplateDetail }>(`${BASE}/${selectedId}`)
      .then((response) => {
        if (cancelled) return
        setDetail(response.data)
        setName(response.data.name)
        setDescription(response.data.description ?? '')
        setCategory(response.data.category ?? '')
        setStatus(response.data.status)
        setIsKickoff(response.data.is_kickoff)
        setQuestions(
          response.data.questions.map((question) => ({
            code: question.code,
            type: question.type,
            label: question.label,
            description: question.description,
            required: question.required,
            config: question.config,
          })),
        )
      })
      .catch((err) => !cancelled && setError(toErrorMessage(err)))

    return () => {
      cancelled = true
    }
  }, [selectedId])

  const create = async () => {
    setSaving(true)
    setError(null)
    try {
      const response = await api.post<{ data: SurveyTemplateDetail }>(BASE, {
        name: t('templates.untitled'),
      })
      await loadList()
      setSelectedId(response.data.id)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  const save = async () => {
    if (!selectedId) return

    setSaving(true)
    setError(null)
    try {
      const response = await api.put<{ data: SurveyTemplateDetail }>(`${BASE}/${selectedId}`, {
        name: name.trim(),
        description: description.trim(),
        category: category.trim(),
        status,
        questions,
        // Sent only when it changed: a marked template that is being archived
        // keeps its mark, and asking for the mark again would be refused.
        ...(detail && isKickoff !== detail.is_kickoff ? { is_kickoff: isKickoff } : {}),
      })
      // Marking one moves the mark off another, so what the list shows and what
      // this form holds both come from the server's answer.
      setDetail(response.data)
      setIsKickoff(response.data.is_kickoff)
      await loadList()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!selectedId) return

    setSaving(true)
    setError(null)
    try {
      await api.delete(`${BASE}/${selectedId}`)
      setSelectedId(null)
      await loadList()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <>
      <PageHeader
        title={t('templates.title')}
        subtitle={t('templates.subtitle')}
        actions={
          <Button onClick={create} loading={saving}>
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('templates.new')}
          </Button>
        }
      />

      {error && <Alert className="mb-6">{error}</Alert>}

      <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Card className="h-fit divide-y divide-gray-100 dark:divide-gray-800">
          {templates.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<RiSurveyLine className="size-6" aria-hidden="true" />}
                title={t('templates.emptyTitle')}
                description={t('templates.emptyDescription')}
              />
            </div>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setSelectedId(template.id)}
                className={cx(
                  'flex w-full items-center gap-3 p-4 text-left transition',
                  template.id === selectedId
                    ? 'bg-primary-50 dark:bg-primary-500/10'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-900',
                  focusRing,
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                    {template.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {t('surveys.questionCount', { count: template.question_count })} ·{' '}
                    {t('templates.versionShort', { version: template.version })} ·{' '}
                    {formatDate(template.updated_at, i18n.language)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant={TEMPLATE_STATUS_TONE[template.status]}>
                    {t(`templates.status_${template.status}`)}
                  </Badge>
                  {template.is_kickoff && (
                    <Badge variant={template.status === 'published' ? 'success' : 'neutral'}>
                      {t('templates.kickoffBadge')}
                    </Badge>
                  )}
                </div>
              </button>
            ))
          )}
        </Card>

        {detail ? (
          <div className="space-y-6">
            <Card className="space-y-6 p-6">
              <Field label={t('templates.name')} required>
                <Input value={name} onChange={(event) => setName(event.target.value)} />
              </Field>

              <Field label={t('templates.description')}>
                <Textarea
                  rows={2}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('templates.category')}>
                  <Input value={category} onChange={(event) => setCategory(event.target.value)} />
                </Field>

                <Field label={t('templates.status')}>
                  <Select
                    value={status}
                    onChange={(event) => {
                      const next = event.target.value as SurveyTemplateStatus
                      setStatus(next)
                      // A mark just asked for cannot outlive the status it needs;
                      // one that is already saved stays, and does nothing.
                      if (next !== 'published' && detail && !detail.is_kickoff) setIsKickoff(false)
                    }}
                    options={(['draft', 'published', 'archived'] as const).map((value) => ({
                      value,
                      label: t(`templates.status_${value}`),
                    }))}
                  />
                </Field>
              </div>
              <div className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-4 dark:border-gray-800">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                    {t('templates.kickoffTitle')}
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {status === 'published'
                      ? t('templates.kickoffDescription')
                      : t('templates.kickoffNeedsPublished')}
                  </p>
                  {isKickoff && status !== 'published' && (
                    <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-500">
                      {t('templates.kickoffInactive')}
                    </p>
                  )}
                </div>
                <Switch
                  checked={isKickoff}
                  onCheckedChange={setIsKickoff}
                  disabled={status !== 'published' && !isKickoff}
                  aria-label={t('templates.kickoffTitle')}
                />
              </div>
            </Card>

            <QuestionBuilder questions={questions} onChange={setQuestions} />

            <div className="flex items-center justify-between">
              <ConfirmDialog
                title={t('templates.deleteTitle')}
                description={t('templates.deleteConfirm')}
                onConfirm={remove}
                trigger={
                  <Button variant="secondary" disabled={saving}>
                    <RiDeleteBinLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                    {t('common.delete')}
                  </Button>
                }
              />
              <Button onClick={save} loading={saving}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <Card className="p-6">
            <EmptyState title={t('templates.selectTitle')} description={t('templates.selectDescription')} />
          </Card>
        )}
      </div>
    </>
  )
}
